import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyledSessionWrapper, StyledSessionBody } from "../session.style";
import styled from "@emotion/styled";
import { VirtuosoMessageListLicense, VirtuosoMessageList } from "@virtuoso.dev/message-list";
import type { ItemLocation, VirtuosoMessageListMethods } from "@virtuoso.dev/message-list";
import { SessionItem, SessionItemContext } from "../session-item/session-item.utils";
import { SessionItemRenderer } from "@/mds/components/sessions/session-item/session-item-renderer";
import { MDSSessionContextValue } from "@/mds/components/sessions/session-context/mds-session-context.types";
import { SessionListFooter } from "@/mds/components/sessions/session-list-footer/session-list-footer";
import { SessionItemKind } from "@/mds/components/sessions/session-item";

type Props = Pick<
  MDSSessionContextValue,
  "sessionItems" | "sessionStatus" | "activeArtifact" | "sessionConfig"
> & {
  containerHeight: number;
};

// Internal component that uses the context
export const MDSSessionList: React.FC<Props> = ({
  sessionConfig,
  sessionStatus,
  sessionItems,
  containerHeight,
}) => {
  const messagesRef = useRef<VirtuosoMessageListMethods<SessionItem, SessionItemContext>>(null);
  const [hasInitiallyRendered, setHasInitiallyRendered] = useState(false);
  const prevSessionItemsRef = useRef<SessionItem[]>([]);

  // Update the virtualized message list when session items change
  // This handles both initial render and subsequent updates with different behaviors:
  // - Initial render: instant positioning with item size cache purging for accurate sizing
  // - Subsequent updates: optimized append vs full replace based on diff
  // - Smooth scrolling animation for new items
  // Note that virtuoso has dedicated "initialLocation" prop that does only part of the job unfortunately
  // The bug was:
  // - render component in deeper hierarchy (like chrome ext). it renders, all good
  // - unmount parent component and mount it back on
  useEffect(() => {
    if (messagesRef.current && sessionItems.length > 0) {
      const isInitialRender = !hasInitiallyRendered;

      const bottomOffset = messagesRef?.current?.getScrollLocation().bottomOffset || 0;
      const shouldAutoScroll = bottomOffset < 100;

      const initialLocation: ItemLocation = {
        index: "LAST",
        align: "end",
        behavior: customSmoothScroll,
        offset: 100,
      };

      messagesRef.current.data.replace(sessionItems, {
        initialLocation: shouldAutoScroll ? initialLocation : undefined,
      });

      if (isInitialRender) {
        setHasInitiallyRendered(true);
      }
    }

    // Update the ref for next comparison
    prevSessionItemsRef.current = sessionItems;
  }, [hasInitiallyRendered, sessionItems]);

  const config = useMemo(
    () => ({ openLinksInNewTab: sessionConfig.openLinksInNewTab }),
    [sessionConfig.openLinksInNewTab]
  );

  const context: SessionItemContext = useMemo(
    () => ({
      status: sessionStatus,
      config,
      lastReasoningGroupId: (() => {
        const last = sessionItems
          .slice()
          .reverse()
          .find(item => item.kind === SessionItemKind.ReasoningGroup);
        return last?.id ?? null;
      })(),
      lastItemId: sessionItems.length > 0 ? sessionItems[sessionItems.length - 1].id : null,
    }),
    [sessionStatus, config, sessionItems]
  );

  return (
    <StyledSessionWrapper>
      {/* Session Body */}
      <StyledSessionBody>
        {/* Session Messages */}
        <MessagesContainer>
          <VirtuosoMessageListLicense licenseKey={import.meta.env.VITE_VIRTUOSO_KEY}>
            <VirtuosoMessageList
              ref={messagesRef}
              ItemContent={SessionItemRenderer}
              context={context}
              initialData={sessionItems}
              computeItemKey={params => params.data.id}
              style={{
                height: containerHeight,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                overflowX: "hidden",
                overflowY: "auto",
                overscrollBehaviorY: "contain",
                paddingLeft: 16,
                paddingRight: 16,
              }}
              shortSizeAlign="bottom"
              Footer={SessionListFooter}
            />
          </VirtuosoMessageListLicense>
        </MessagesContainer>
      </StyledSessionBody>
    </StyledSessionWrapper>
  );
};

const MessagesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  /* Hide scrollbars */
  & [data-virtuoso-scroller] {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }

  /* Fade effect at top and bottom */
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    top: 0;
    height: 24px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }

  &::after {
    bottom: 0;
    height: 8px;
    background: linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }
`;

const customSmoothScroll = () => {
  const easeOutQuad = (x: number): number => 1 - (1 - x) * (1 - x);

  return {
    // Increase the animation frame count to smoothen and slow down the scroll.
    animationFrameCount: 10,
    easing: easeOutQuad,
  };
};
